import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Download } from 'lucide-react';
import api from '../utils/api';
import { fmtDate, fmtCurrency, canDelete } from '../utils/helpers';
import { DataTable, SearchBar, StatusBadge, Modal, FormField } from './Table';
import ClientSearchDropdown from './ClientSearchDropdown';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../context/AuthContext';
import { downloadExport } from '../utils/exportUtils';
import toast from 'react-hot-toast';

const CHALLAN_TYPES = {
  tds: ['TDS Payment'],
  itr: ['Advance Tax', 'Self Assessment Tax', 'Regular Assessment Tax'],
};
const ALL_TYPES = ['TDS Payment', 'Advance Tax', 'Self Assessment Tax', 'Regular Assessment Tax'];
const SECTIONS = ['192', '194A', '194B', '194C', '194D', '194G', '194H', '194I', '194J', '194LA', '194Q', '206C', 'Other'];
const STATUSES = ['Paid', 'Pending', 'Verified'];
const QUARTERS = ['Q1 (Apr–Jun)', 'Q2 (Jul–Sep)', 'Q3 (Oct–Dec)', 'Q4 (Jan–Mar)'];
const AY_OPTIONS = ['2025-26', '2026-27', '2024-25', '2023-24'];

export default function ChallanTab({ module }) {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ module, status: 'Paid' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canDel = canDelete(user);

  const load = () => {
    const params = { module };
    if (dateFrom) params.from_date = dateFrom;
    if (dateTo) params.to_date = dateTo;
    if (statusFilter) params.status = statusFilter;
    api.get('/challans', { params }).then(r => setData(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [module, dateFrom, dateTo, statusFilter]);

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/challans/${editing.id}`, { ...form, module });
      else await api.post('/challans', { ...form, module });
      toast.success('Saved'); setShowModal(false); setEditing(null); setForm({ module, status: 'Paid' }); load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/challans/${deleteTarget.id}`); toast.success('Deleted'); setDeleteTarget(null); load(); }
    catch { toast.error('Failed'); }
  };

  const filtered = data.filter(d =>
    !search || d.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.bsr_code?.includes(search) || d.serial_number?.includes(search)
  );

  const totalAmount = filtered.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const types = [...(CHALLAN_TYPES[module] || []), ...(module !== 'tds' ? [] : [])];

  const columns = [
    { key: 'client_name', label: 'Client', render: (v, r) => (
      <div><div className="font-medium text-sm">{v}</div>{r.client_code && <div className="text-xs text-gray-400 font-mono">{r.client_code}</div>}</div>
    )},
    { key: 'challan_type', label: 'Type', render: v => <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{v}</span> },
    { key: 'bsr_code', label: 'BSR Code' },
    { key: 'serial_number', label: 'Serial No.' },
    { key: 'payment_date', label: 'Date', render: v => fmtDate(v) },
    { key: 'amount', label: 'Amount', render: v => <span className="font-semibold text-green-700">{fmtCurrency(v)}</span> },
    { key: 'period', label: 'Period/AY', render: (v, r) => v || r.ay || '—' },
    { key: 'section', label: 'Section', render: v => v ? <span className="font-mono text-xs">{v}</span> : '—' },
    { key: 'bank_name', label: 'Bank' },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex gap-2">
        <button onClick={() => { setEditing(row); setForm({ ...row }); setShowModal(true); }} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
        {canDel && <button onClick={() => setDeleteTarget(row)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>}
      </div>
    )},
  ];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="card text-center py-3">
          <div className="text-xs text-gray-500">Total Challans</div>
          <div className="text-xl font-bold text-[#1e3a5f] mt-1">{filtered.length}</div>
        </div>
        <div className="card text-center py-3">
          <div className="text-xs text-gray-500">Total Amount</div>
          <div className="text-xl font-bold text-green-700 mt-1">{fmtCurrency(totalAmount)}</div>
        </div>
        <div className="card text-center py-3">
          <div className="text-xs text-gray-500">Pending</div>
          <div className="text-xl font-bold text-orange-600 mt-1">{filtered.filter(r => r.status === 'Pending').length}</div>
        </div>
        <div className="card text-center py-3">
          <div className="text-xs text-gray-500">Paid</div>
          <div className="text-xl font-bold text-green-600 mt-1">{filtered.filter(r => r.status === 'Paid').length}</div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4 items-end justify-between">
          <div className="flex flex-wrap gap-3">
            <SearchBar value={search} onChange={setSearch} placeholder="Search client, BSR, serial..." />
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input-field w-36" placeholder="From" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input-field w-36" placeholder="To" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-32">
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => downloadExport('/challans/export', { module }, `KUA_Challans_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            <button onClick={() => { setEditing(null); setForm({ module, status: 'Paid', challan_type: ALL_TYPES[0] }); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Challan</button>
          </div>
        </div>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit Challan' : 'Add Challan'} onClose={() => { setShowModal(false); setEditing(null); }} size="lg">
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-3">
            <FormField label="Client" required className="col-span-2">
              <ClientSearchDropdown value={form.client_id || ''} initialLabel={form.client_name || ''} onChange={(id, name) => setForm(f => ({ ...f, client_id: id, client_name: name }))} required />
            </FormField>
            <FormField label="Challan Type">
              <select value={form.challan_type || ALL_TYPES[0]} onChange={e => setForm(f => ({ ...f, challan_type: e.target.value }))} className="input-field">
                {ALL_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select value={form.status || 'Paid'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="BSR Code"><input value={form.bsr_code || ''} onChange={e => setForm(f => ({ ...f, bsr_code: e.target.value }))} className="input-field" placeholder="7-digit BSR code" /></FormField>
            <FormField label="Challan Serial No."><input value={form.serial_number || ''} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} className="input-field" /></FormField>
            <FormField label="Date of Payment">
              <input type="date" value={form.payment_date || ''} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} className="input-field" />
            </FormField>
            <FormField label="Amount (₹)">
              <input type="number" step="0.01" value={form.amount || ''} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="input-field" />
            </FormField>
            <FormField label="Section (TDS)">
              <select value={form.section || ''} onChange={e => setForm(f => ({ ...f, section: e.target.value }))} className="input-field">
                <option value="">Select</option>
                {SECTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Period / Quarter">
              <input value={form.period || ''} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} className="input-field" placeholder="e.g. Apr 2025 or Q1 2025-26" />
            </FormField>
            <FormField label="Assessment Year">
              <select value={form.ay || ''} onChange={e => setForm(f => ({ ...f, ay: e.target.value }))} className="input-field">
                <option value="">Select</option>
                {AY_OPTIONS.map(a => <option key={a}>{a}</option>)}
              </select>
            </FormField>
            <FormField label="Bank Name">
              <input value={form.bank_name || ''} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} className="input-field" placeholder="e.g. HDFC Bank" />
            </FormField>
            <FormField label="Remarks">
              <input value={form.remarks || ''} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className="input-field" />
            </FormField>
            <div className="col-span-2 flex gap-3">
              <button type="submit" className="btn-primary flex-1">Save</button>
              <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
      {deleteTarget && <ConfirmModal message={`Delete challan for ${deleteTarget.client_name}?`} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}
    </div>
  );
}
